import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  Link,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Save, Download, DeleteOutline } from '@mui/icons-material';
import { UserStatusBadge, UserRoleBadge, OutlinedBadge } from '@tms/shared-ui';
import UserAnalyticsPanel from '../components/users/UserAnalyticsPanel';
import UserOverviewPanel from '../components/users/UserOverviewPanel';
import UserEngagementInsights from '../components/users/UserEngagementInsights';
import UserListAvatar from '../components/users/UserListAvatar';
import {
  exportUserDataJson,
  getAuditLogs,
  getTeacherSuggestions,
  getUserSummary,
  type AuditLogEntry,
  type TeacherSuggestion,
  type UserSummary,
} from '../services/adminAnalyticsApi';
import {
  getUserById,
  getAllUsers,
  updateUserProfile,
  updateContactInfos,
  updateUserAccount,
  resetUserPassword,
  assignTeacher,
  unassignTeacherFromStudent,
  deleteUserAndData,
  type UserDetail as UserDetailType,
  type AdminUserRow,
} from '../services/usersApi';
import ConfirmDeleteUserDialog from '../components/users/ConfirmDeleteUserDialog';
import {
  getUserSubscriptions,
  getUserSubscriptionHistory,
  getUserTransactions,
  cancelSubscription,
  reactivateSubscription,
  extendTrial,
  grantCompAccess,
} from '../services/adminSubscriptionsApi';
import { getUserProgressionStats, getUserProgressions } from '../services/progressionApi';
import {
  adjustUserExperience,
  advanceUserLevel,
  completeUserChallenge,
  getBadges,
  getExperienceHistory,
  getUserChallenges,
  grantUserBadge,
  initializeUserProgression,
  revokeUserBadge,
  type Badge,
  type ExperienceHistoryEntry,
  type UserChallengeItem,
} from '../services/progressionAdminApi';
import { useLanguage } from '../hooks/useLanguage';
import { useAdminScope } from '../context/AdminScopeContext';
import { getStudentSessions, type CourseSessionRow } from '../services/sessionsApi';
import { downloadCsv } from '../utils/csv';
import { ContentPageLayout } from '../components/Shared';

const TAB_KEYS = ['overview', 'profile', 'relations', 'subscription', 'progression', 'sessions', 'activity', 'audit'] as const;
type TabKey = (typeof TAB_KEYS)[number];

const LIMITED_USER_TABS: TabKey[] = ['overview', 'profile', 'relations', 'subscription', 'sessions', 'activity'];

const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview',
  profile: 'Profile',
  relations: 'Relations',
  subscription: 'Subscription',
  progression: 'Progression',
  sessions: 'Sessions',
  activity: 'Activity',
  audit: 'Audit',
};

const PROG_PAGE_SIZE = 10;

const GATEWAY_LABELS: Record<string, string> = {
  paypal: 'PayPal',
  paddle: 'Paddle',
  polar: 'Polar',
  stripe: 'Stripe',
  trial: 'Trial',
  apple_pay: 'Apple Pay',
  google_pay: 'Google Pay',
  credit_card: 'Credit card',
  bank_transfer: 'Bank transfer',
};

const formatGateway = (value?: string | null): string => {
  if (!value) return '—';
  return GATEWAY_LABELS[value] || value.charAt(0).toUpperCase() + value.slice(1);
};

function isTeacherRoleSubscription(subscription: any): boolean {
  return subscription?.metadata?.source === 'teacher_role'
    || (Array.isArray(subscription?.tags) && subscription.tags.includes('teacher_role'));
}

const ROLES = ['Admin', 'Student', 'Teacher'];
const STATUSES = ['New', 'Verified', 'Registered', 'Disactivated', 'Banned'];

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { navigateWithLang } = useLanguage();
  const { scope, userId: currentUserId } = useAdminScope();

  const visibleTabs = useMemo(
    () => (scope === 'full' ? [...TAB_KEYS] : LIMITED_USER_TABS),
    [scope]
  );

  const tabParam = searchParams.get('tab') as TabKey | null;
  const resolvedTab = tabParam && visibleTabs.includes(tabParam) ? tabParam : 'overview';
  const tabIndex = Math.max(0, visibleTabs.indexOf(resolvedTab));
  const activeTabKey = visibleTabs[tabIndex] ?? 'overview';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<UserDetailType | null>(null);
  const [allUsers, setAllUsers] = useState<AdminUserRow[]>([]);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);

  // Edit form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [nickname, setNickname] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [assignTeacherId, setAssignTeacherId] = useState('');
  const [teacherAllowedFileLevels, setTeacherAllowedFileLevels] = useState<number[]>([1, 2, 3]);

  // Tab data
  const [subs, setSubs] = useState<any>(null);
  const [subHistory, setSubHistory] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [progStats, setProgStats] = useState<any>(null);
  const [progressions, setProgressions] = useState<any[]>([]);
  const [progPage, setProgPage] = useState(0);
  const [progTotal, setProgTotal] = useState(0);
  const [sessions, setSessions] = useState<CourseSessionRow[]>([]);
  const [tabLoading, setTabLoading] = useState(false);
  const [extendDays, setExtendDays] = useState('7');
  const [compDays, setCompDays] = useState('30');
  const [initLevel, setInitLevel] = useState('1');
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(false);
  const [xpHistory, setXpHistory] = useState<ExperienceHistoryEntry[]>([]);
  const [xpAdjustAmount, setXpAdjustAmount] = useState('');
  const [xpAdjustReason, setXpAdjustReason] = useState('');
  const [xpAdjustLoading, setXpAdjustLoading] = useState(false);
  const [manualBadges, setManualBadges] = useState<Badge[]>([]);
  const [badgeActionId, setBadgeActionId] = useState<string | null>(null);
  const [userChallenges, setUserChallenges] = useState<UserChallengeItem[]>([]);
  const [challengeActionId, setChallengeActionId] = useState<string | null>(null);
  const [summary, setSummary] = useState<UserSummary | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [teacherSuggestions, setTeacherSuggestions] = useState<TeacherSuggestion[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const auth = user?.userAuth;
  const infos = user?.userInfos;

  const avatarUrl = useMemo(() => {
    if (summary?.identity.avatarUrl) return summary.identity.avatarUrl;
    const fromProfile = user?.userAvatar?.avatarUrl;
    if (fromProfile) return fromProfile;
    const fromList = allUsers.find((u) => u._id === userId)?.avatarUrl;
    return fromList;
  }, [summary, user, allUsers, userId]);

  const csvBase = useMemo(() => {
    const name = summary?.identity.displayName || auth?.nickname || auth?.email || userId || 'user';
    return `user-${String(name).replace(/[^\w.-]+/g, '_')}`;
  }, [summary, auth, userId]);

  const sessionTeacherName = (s: CourseSessionRow) =>
    typeof s.profId === 'object' && s.profId !== null
      ? (s.profId as any).nickname || (s.profId as any).email || ''
      : (s.profId as any) || '';

  const handleDownloadProgressionsCsv = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await getUserProgressions(userId, progTotal || 1000, 0);
      const rows = (res.data?.progressions || []).map((p) => ({
        code: p.documentCode || '',
        domain: p.documentDomain || '',
        status: p.isCompleted ? 'completed' : p.status || '',
        lastAccessed: p.lastAccessedAt ? new Date(p.lastAccessedAt).toISOString() : '',
      }));
      downloadCsv(`${csvBase}-progression`, rows, ['code', 'domain', 'status', 'lastAccessed']);
    } catch (e: any) {
      setSaveMsg(e?.message || 'Failed to export progression');
    }
  }, [userId, progTotal, csvBase]);

  const teachers = useMemo(
    () => allUsers.filter((u) => u.role === 'Teacher' || u.isTeacher),
    [allUsers]
  );

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const [userRes, usersRes, summaryRes] = await Promise.all([
        getUserById(userId),
        getAllUsers(),
        getUserSummary(userId).catch(() => null),
      ]);
      setUser(userRes.user);
      setAllUsers(usersRes.data?.users || []);
      if (summaryRes?.data) setSummary(summaryRes.data);

      const ua = userRes.user?.userAuth;
      const ui = userRes.user?.userInfos;
      setNickname(ua?.nickname || '');
      setRole(ua?.role || '');
      setStatus(ua?.status || '');
      setTeacherAllowedFileLevels(
        Array.isArray(ua?.teacherAllowedFileLevels) && ua.teacherAllowedFileLevels.length > 0
          ? [...ua.teacherAllowedFileLevels].sort((a, b) => a - b)
          : [1, 2, 3],
      );
      setFirstName(ui?.firstName || '');
      setLastName(ui?.lastName || '');
      setCity(ui?.city || '');
      setPostalCode(ui?.postalCode || '');
      setPhone(ui?.phone || '');
      setAddress1(ui?.address1 || '');
      setAddress2(ui?.address2 || '');
    } catch (e: any) {
      setError(e?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const loadTabData = useCallback(async (tab: TabKey) => {
    if (!userId) return;
    setTabLoading(true);
    try {
      if (tab === 'overview') {
        const res = await getUserSummary(userId);
        setSummary(res.data);
      } else if (tab === 'subscription') {
        const [s, h, t] = await Promise.all([
          getUserSubscriptions(userId),
          getUserSubscriptionHistory(userId),
          getUserTransactions(userId),
        ]);
        setSubs(s);
        setSubHistory(h.history || []);
        setTransactions(t.transactions || []);
      } else if (tab === 'progression') {
        const [stats, progs, history, badgesRes, challengesRes] = await Promise.all([
          getUserProgressionStats(userId),
          getUserProgressions(userId, PROG_PAGE_SIZE, 0),
          getExperienceHistory(userId, { limit: 30 }),
          getBadges(),
          getUserChallenges(userId),
        ]);
        setProgStats(stats.data?.stats);
        setProgressions(progs.data?.progressions || []);
        setXpHistory(history.data?.history || []);
        setProgTotal(progs.data?.total ?? 0);
        setProgPage(0);
        const adminBadges = (badgesRes.data?.badges || []).filter((b) => b.tags?.includes('admin-grant'));
        setManualBadges(adminBadges);
        setUserChallenges(challengesRes.data?.challenges || []);
      } else if (tab === 'sessions') {
        const res = await getStudentSessions(userId, 50);
        const list = res.data?.sessions || (res as any).sessions || [];
        setSessions(list);
      } else if (tab === 'audit') {
        const res = await getAuditLogs({ userId, limit: 50 });
        setAuditLogs(res.data?.logs || []);
      } else if (tab === 'relations' && user?.userAuth?.role === 'Student') {
        const res = await getTeacherSuggestions(userId);
        setTeacherSuggestions(res.data?.suggestions || []);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load tab data');
    } finally {
      setTabLoading(false);
    }
  }, [userId, user?.userAuth?.role]);

  const loadProgressionsPage = useCallback(async (page: number) => {
    if (!userId) return;
    setTabLoading(true);
    try {
      const progs = await getUserProgressions(userId, PROG_PAGE_SIZE, page * PROG_PAGE_SIZE);
      setProgressions(progs.data?.progressions || []);
      setProgTotal(progs.data?.total ?? 0);
      setProgPage(page);
    } catch (e: any) {
      setError(e?.message || 'Failed to load progressions');
    } finally {
      setTabLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    const key = activeTabKey;
    if (
      key === 'overview' ||
      key === 'subscription' ||
      key === 'progression' ||
      key === 'sessions' ||
      key === 'audit' ||
      key === 'relations'
    ) {
      loadTabData(key);
    }
  }, [activeTabKey, loadTabData]);

  const handleTabChange = (_: React.SyntheticEvent, idx: number) => {
    setSearchParams({ tab: visibleTabs[idx] });
  };

  const handleExportUser = async () => {
    if (!userId) return;
    setExportLoading(true);
    try {
      const res = await exportUserDataJson(userId);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `user-${userId}-export.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      setSaveMsg('User data exported.');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const openTab = (tab: string) => {
    if (visibleTabs.includes(tab as TabKey)) {
      setSearchParams({ tab });
    }
  };

  const handleSaveProfile = async () => {
    if (!userId) return;
    setSaveMsg(null);
    try {
      await updateUserProfile(userId, { firstName, lastName, city, postalCode });
      await updateContactInfos(userId, { phone, address1, address2 });
      await updateUserAccount(userId, { nickname, role, status });
      setSaveMsg('Profile saved.');
      await loadUser();
    } catch (e: any) {
      const validation = Array.isArray(e?.data?.errors)
        ? e.data.errors.map((err: { msg?: string }) => err.msg).filter(Boolean).join(' ')
        : '';
      setSaveMsg(validation || e?.message || 'Save failed');
    }
  };

  const handleSaveTeacherDocumentLevels = async () => {
    if (!userId) return;
    setSaveMsg(null);
    try {
      await updateUserAccount(userId, {
        teacherAllowedFileLevels: [...teacherAllowedFileLevels].sort((a, b) => a - b),
      });
      setSaveMsg('Teacher document level permissions saved.');
      await loadUser();
    } catch (e: any) {
      setSaveMsg(e?.message || 'Save failed');
    }
  };

  const toggleTeacherAllowedLevel = (level: number) => {
    setTeacherAllowedFileLevels((current) => {
      if (current.includes(level)) {
        const next = current.filter((value) => value !== level);
        return next.length > 0 ? next : current;
      }
      return [...current, level].sort((a, b) => a - b);
    });
  };

  const canDeleteThisUser = Boolean(
    scope === 'full'
    && userId
    && auth
    && auth.role !== 'Admin'
    && auth._id !== currentUserId,
  );

  const handleDeleteUser = async (confirmEmail: string) => {
    if (!userId) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteUserAndData(userId, confirmEmail);
      navigateWithLang('/users');
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!userId || !newPassword) return;
    setSaveMsg(null);
    try {
      await resetUserPassword(userId, newPassword);
      setNewPassword('');
      setSaveMsg('Password updated.');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Password reset failed');
    }
  };

  const handleAssignTeacher = async () => {
    if (!userId || !assignTeacherId) return;
    try {
      await assignTeacher(userId, assignTeacherId);
      setAssignTeacherId('');
      await loadUser();
      setSaveMsg('Teacher assigned.');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Assign failed');
    }
  };

  const handleUnassignTeacher = async () => {
    if (!userId) return;
    try {
      await unassignTeacherFromStudent(userId);
      await loadUser();
      setSaveMsg('Teacher unassigned.');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Unassign failed');
    }
  };

  const handleCancelSub = async (subscriptionId: string) => {
    if (!userId) return;
    try {
      await cancelSubscription(userId, subscriptionId, 'Canceled by admin');
      await loadTabData('subscription');
      setSaveMsg('Subscription canceled.');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Cancel failed');
    }
  };

  const handleReactivateSub = async (subscriptionId: string) => {
    if (!userId) return;
    try {
      await reactivateSubscription(userId, subscriptionId);
      await loadTabData('subscription');
      setSaveMsg('Subscription reactivated.');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Reactivate failed');
    }
  };

  const handleExtendTrial = async (subscriptionId: string) => {
    if (!userId) return;
    const days = parseInt(extendDays, 10);
    if (!days || days < 1) {
      setSaveMsg('Enter valid trial extension days (1–90).');
      return;
    }
    try {
      await extendTrial(userId, subscriptionId, days);
      await loadTabData('subscription');
      setSaveMsg(`Trial extended by ${days} day(s).`);
    } catch (e: any) {
      setSaveMsg(e?.message || 'Extend trial failed');
    }
  };

  const handleXpAdjust = async () => {
    if (!userId) return;
    const amount = parseInt(xpAdjustAmount, 10);
    if (!amount || Number.isNaN(amount)) {
      setSaveMsg('Enter a non-zero XP adjustment amount.');
      return;
    }
    setXpAdjustLoading(true);
    try {
      const res = await adjustUserExperience(userId, amount, xpAdjustReason.trim() || undefined);
      setSaveMsg(res.message || 'XP adjusted.');
      setXpAdjustAmount('');
      setXpAdjustReason('');
      await loadTabData('progression');
    } catch (e: any) {
      setSaveMsg(e?.message || 'XP adjust failed');
    } finally {
      setXpAdjustLoading(false);
    }
  };

  const handleAdvanceLevel = async () => {
    if (!userId) return;
    if (!window.confirm('Promote this user to the next level? Requires all advancement rules for their current level.')) {
      return;
    }
    setAdvanceLoading(true);
    setSaveMsg(null);
    try {
      const res = await advanceUserLevel(userId);
      setSaveMsg(res.message || 'User advanced.');
      await loadTabData('progression');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Advance failed');
    } finally {
      setAdvanceLoading(false);
    }
  };

  const userEarnedBadgeIds = useMemo(() => {
    const entries = (progStats?.badges || []) as Array<{ badgeId?: Badge | string }>;
    return new Set(
      entries
        .map((entry) => {
          const b = entry.badgeId;
          if (typeof b === 'string') return b;
          return b?._id;
        })
        .filter(Boolean),
    );
  }, [progStats?.badges]);

  const adminVerifiedChallenges = useMemo(
    () =>
      userChallenges.filter(({ challenge }) =>
        (challenge.conditions || []).some((c) => c.type === 'admin_verification'),
      ),
    [userChallenges],
  );

  const handleCompleteUserChallenge = async (challengeId: string, challengeName: string) => {
    if (!userId) return;
    if (!window.confirm(`Mark "${challengeName}" complete for this user? They will receive challenge rewards (XP + badge).`)) {
      return;
    }
    setChallengeActionId(challengeId);
    setSaveMsg(null);
    try {
      const res = await completeUserChallenge(userId, challengeId);
      const xp = res.data?.award?.xpGranted;
      const badges = res.data?.award?.badgesGranted;
      const extra =
        xp || badges
          ? ` (+${xp || 0} XP${badges ? `, ${badges} badge(s)` : ''})`
          : '';
      setSaveMsg((res.message || 'Challenge completed.') + extra);
      await loadTabData('progression');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Complete challenge failed');
    } finally {
      setChallengeActionId(null);
    }
  };

  const handleGrantManualBadge = async (badgeId: string, badgeName: string) => {
    if (!userId) return;
    if (!window.confirm(`Grant "${badgeName}" to this user?`)) return;
    setBadgeActionId(badgeId);
    setSaveMsg(null);
    try {
      const res = await grantUserBadge(userId, badgeId);
      setSaveMsg(res.message || 'Badge granted.');
      await loadTabData('progression');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Grant badge failed');
    } finally {
      setBadgeActionId(null);
    }
  };

  const handleRevokeManualBadge = async (badgeId: string, badgeName: string) => {
    if (!userId) return;
    if (!window.confirm(`Revoke "${badgeName}" from this user?`)) return;
    setBadgeActionId(badgeId);
    setSaveMsg(null);
    try {
      const res = await revokeUserBadge(userId, badgeId);
      setSaveMsg(res.message || 'Badge revoked.');
      await loadTabData('progression');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Revoke badge failed');
    } finally {
      setBadgeActionId(null);
    }
  };

  const handleInitializeProgression = async () => {
    if (!userId) return;
    const level = parseInt(initLevel, 10) || 1;
    if (!window.confirm(`Initialize progression for this user at level ${level}? Fails if they already have progression rows.`)) {
      return;
    }
    setInitLoading(true);
    setSaveMsg(null);
    try {
      const res = await initializeUserProgression(userId, level);
      setSaveMsg(res.message || `Initialized ${res.data?.documentsInitialized ?? 0} documents.`);
      await loadTabData('progression');
    } catch (e: any) {
      setSaveMsg(e?.message || 'Initialize failed');
    } finally {
      setInitLoading(false);
    }
  };

  const handleGrantComp = async () => {
    if (!userId) return;
    const days = parseInt(compDays, 10);
    if (!days || days < 1) {
      setSaveMsg('Enter valid comp days (1–365).');
      return;
    }
    try {
      await grantCompAccess(userId, days);
      await loadTabData('subscription');
      setSaveMsg(`Granted ${days} day(s) complimentary access.`);
    } catch (e: any) {
      setSaveMsg(e?.message || 'Grant access failed');
    }
  };

  if (loading) {
    return (
      <ContentPageLayout>
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
      </ContentPageLayout>
    );
  }

  if (error && !user) {
    return (
      <ContentPageLayout>
      <Box p={2}>
        <Alert severity="error">{error}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('../users')}>
          Back to users
        </Button>
      </Box>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('../users')} sx={{ mb: 2 }} size="small">
        Back to users
      </Button>

      <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap" mb={1}>
        <UserListAvatar
          avatarUrl={avatarUrl}
          firstName={infos?.firstName}
          lastName={infos?.lastName}
          email={auth?.email}
          nickname={auth?.nickname}
          size={64}
        />
        <Box flex={1} minWidth={200}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h5" color="primary">
              {summary?.identity.displayName || auth?.nickname || auth?.email}
            </Typography>
            {auth?.role ? <UserRoleBadge role={auth.role} /> : null}
            {auth?.status ? <UserStatusBadge status={auth.status} /> : null}
          </Box>
          <Typography variant="body2" color="text.secondary">
            {auth?.email}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registered{' '}
            {auth?.createdAt
              ? new Date(auth.createdAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : '—'}
            {' · '}
            Last active{' '}
            {summary?.engagement.lastActivityAt
              ? new Date(summary.engagement.lastActivityAt).toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })
              : '—'}
          </Typography>
          {summary ? (
            <Box display="flex" flexWrap="wrap" gap={0.75} mt={1}>
              <OutlinedBadge
                size="small"
                label={`Subscription: ${summary.subscription.state}`}
                color={
                  summary.subscription.state === 'paid'
                    ? 'info'
                    : summary.subscription.state === 'trial' || summary.subscription.state === 'grace' || summary.subscription.state === 'none'
                      ? 'error'
                      : 'default'
                }
              />
              <OutlinedBadge
                size="small"
                label={
                  summary.engagement.daysSinceActivity != null && summary.engagement.daysSinceActivity <= 7
                    ? 'Engagement: Active'
                    : 'Engagement: Low'
                }
                color={
                  summary.engagement.daysSinceActivity != null && summary.engagement.daysSinceActivity <= 7
                    ? 'info'
                    : 'warning'
                }
              />
              {auth?.role === 'Student' ? (
                <OutlinedBadge
                  size="small"
                  label={summary.teacher.assigned ? 'Teacher: Assigned' : 'Teacher: None'}
                  color={summary.teacher.assigned ? 'info' : 'warning'}
                />
              ) : null}
              <OutlinedBadge
                size="small"
                label={
                  summary.risk.level === 'none'
                    ? 'Risk: None'
                    : `Risk: ${summary.risk.flags.length}`
                }
                color={
                  summary.risk.level === 'high'
                    ? 'error'
                    : summary.risk.level === 'medium'
                      ? 'warning'
                      : 'success'
                }
              />
            </Box>
          ) : null}
        </Box>
      </Box>

      {saveMsg ? (
        <Alert severity={saveMsg.includes('fail') ? 'error' : 'success'} sx={{ mt: 1 }} onClose={() => setSaveMsg(null)}>
          {saveMsg}
        </Alert>
      ) : null}

      <Tabs value={tabIndex} onChange={handleTabChange} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        {visibleTabs.map((key) => (
          <Tab key={key} label={TAB_LABELS[key]} />
        ))}
      </Tabs>

      <Box mt={2}>
        {activeTabKey === 'overview' && (
          <Box>
            {tabLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : summary ? (
              <>
                <Box display="flex" justifyContent="flex-end" mb={1}>
                  <DownloadCsvButton
                    onClick={() => {
                      const rows: Array<Record<string, unknown>> = [];
                      Object.entries(summary as Record<string, any>).forEach(([section, obj]) => {
                        if (obj && typeof obj === 'object') {
                          Object.entries(obj).forEach(([metric, value]) => {
                            rows.push({
                              section,
                              metric,
                              value:
                                value == null
                                  ? ''
                                  : typeof value === 'object'
                                    ? JSON.stringify(value)
                                    : value,
                            });
                          });
                        } else {
                          rows.push({ section, metric: section, value: obj });
                        }
                      });
                      downloadCsv(`${csvBase}-overview`, rows, ['section', 'metric', 'value']);
                    }}
                  />
                </Box>
                {userId ? <UserEngagementInsights userId={userId} /> : null}
                <UserOverviewPanel
                  summary={summary}
                  onOpenTab={openTab}
                  onExport={handleExportUser}
                  exportLoading={exportLoading}
                />
              </>
            ) : (
              <Typography color="text.secondary">No summary available.</Typography>
            )}
          </Box>
        )}

        {activeTabKey === 'profile' && (
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" color="primary">
                Edit profile
              </Typography>
              <DownloadCsvButton
                onClick={() => {
                  const rows = [
                    { field: 'nickname', value: nickname },
                    { field: 'role', value: role },
                    { field: 'status', value: status },
                    { field: 'email', value: auth?.email || '' },
                    { field: 'firstName', value: firstName },
                    { field: 'lastName', value: lastName },
                    { field: 'city', value: city },
                    { field: 'postalCode', value: postalCode },
                    { field: 'phone', value: phone },
                    { field: 'address1', value: address1 },
                    { field: 'address2', value: address2 },
                  ];
                  downloadCsv(`${csvBase}-profile`, rows, ['field', 'value']);
                }}
              />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select fullWidth size="small" label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField select fullWidth size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="City" value={city} onChange={(e) => setCity(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Postal code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Address 1" value={address1} onChange={(e) => setAddress1(e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Address 2" value={address2} onChange={(e) => setAddress2(e.target.value)} />
              </Grid>
            </Grid>
            <Button startIcon={<Save />} variant="contained" sx={{ mt: 2 }} onClick={handleSaveProfile}>
              Save profile
            </Button>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" color="primary" gutterBottom>
              Reset password
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
              <TextField
                size="small"
                type="password"
                label="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button variant="outlined" onClick={handleResetPassword} disabled={newPassword.length < 6}>
                Set password
              </Button>
            </Box>
          </Paper>
        )}

        {activeTabKey === 'relations' && (
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" color="primary.main">Teacher</Typography>
              <DownloadCsvButton
                onClick={() => {
                  const rows: Array<Record<string, unknown>> = [];
                  if (auth?.teacher) {
                    rows.push({
                      relation: 'teacher',
                      name:
                        auth.teacher.teacherNickname ||
                        `${auth.teacher.teacherFirstName || ''} ${auth.teacher.teacherLastName || ''}`.trim(),
                      id: (auth.teacher as any).teacherId || '',
                    });
                  }
                  (auth?.students || []).forEach((st: any) => {
                    rows.push({
                      relation: 'student',
                      name:
                        st.studentNickname ||
                        `${st.studentFirstName || ''} ${st.studentLastName || ''}`.trim(),
                      id: st.studentId || '',
                    });
                  });
                  teacherSuggestions.forEach((s) => {
                    rows.push({
                      relation: 'suggested_teacher',
                      name: s.nickname || s.email || '',
                      id: s.teacherId || '',
                    });
                  });
                  downloadCsv(`${csvBase}-relations`, rows, ['relation', 'name', 'id']);
                }}
                disabled={!auth?.teacher && !(auth?.students || []).length && !teacherSuggestions.length}
              />
            </Box>
            {auth?.teacher ? (
              <Box mb={2}>
                <Typography>
                  {auth.teacher.teacherNickname || auth.teacher.teacherFirstName}{' '}
                  {auth.teacher.teacherLastName}
                </Typography>
                <Button size="small" color="warning" onClick={handleUnassignTeacher} sx={{ mt: 1 }}>
                  Unassign teacher
                </Button>
              </Box>
            ) : (
              <Typography color="text.secondary" gutterBottom>
                No teacher assigned.
              </Typography>
            )}
            {auth?.role === 'Student' ? (
              <>
                <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={2}>
                  <TextField
                    select
                    size="small"
                    label="Assign teacher"
                    value={assignTeacherId}
                    onChange={(e) => setAssignTeacherId(e.target.value)}
                    sx={{ minWidth: 240 }}
                  >
                    {teachers.map((t) => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.nickname || t.email}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button variant="contained" onClick={handleAssignTeacher} disabled={!assignTeacherId}>
                    Assign
                  </Button>
                </Box>
                {teacherSuggestions.length > 0 ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" color="primary.main" gutterBottom>
                      Suggested teachers
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: 'primary.main' }}>Teacher</TableCell>
                          <TableCell sx={{ color: 'primary.main' }}>Students</TableCell>
                          <TableCell sx={{ color: 'primary.main' }}>Match</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {teacherSuggestions.map((s) => (
                          <TableRow key={s.teacherId}>
                            <TableCell>{s.nickname || s.email}</TableCell>
                            <TableCell>{s.studentCount}</TableCell>
                            <TableCell>{s.reasons.join(' · ')}</TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => {
                                  setAssignTeacherId(s.teacherId);
                                }}
                              >
                                Select
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : null}
              </>
            ) : null}

            {(auth?.role === 'Teacher' || auth?.isTeacher) &&
            auth.students &&
            auth.students.length > 0 ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Students ({auth.students.length})
                </Typography>
                <ListLinks students={auth.students} />
              </>
            ) : null}

            {(auth?.role === 'Teacher' || auth?.isTeacher) ? (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Document permissions by level
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Controls which documents this teacher can view, share, and add to students.
                  Default is levels 1 to 3.
                </Typography>
                <FormGroup row>
                  {[1, 2, 3, 4, 5].map((level) => (
                    <FormControlLabel
                      key={level}
                      control={(
                        <Checkbox
                          checked={teacherAllowedFileLevels.includes(level)}
                          onChange={() => toggleTeacherAllowedLevel(level)}
                        />
                      )}
                      label={`Level ${level}`}
                    />
                  ))}
                </FormGroup>
                <Button
                  startIcon={<Save />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSaveTeacherDocumentLevels}
                >
                  Save document permissions
                </Button>
              </>
            ) : null}
            {canDeleteThisUser ? (
              <Box mt={4} pt={2} borderTop={1} borderColor="divider">
                <Typography variant="subtitle1" color="error" fontWeight={600}>
                  Delete user
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Permanently remove this account and attached personal data. Super admin only.
                </Typography>
                <Button
                  startIcon={<DeleteOutline />}
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteOpen(true);
                  }}
                >
                  Delete user and attached data
                </Button>
              </Box>
            ) : null}
          </Paper>
        )}

        {activeTabKey === 'subscription' && (
          <Paper sx={{ p: 2 }}>
            {tabLoading ? <CircularProgress size={24} /> : null}
            <Typography variant="h6" color="primary" gutterBottom>
              Current subscription
            </Typography>
            {subs?.currentSubscription ? (
              <Box mb={2}>
                <Typography>
                  Status: <strong>{subs.currentSubscription.status}</strong>
                  {subs.currentSubscription.planId?.name ? ` · ${subs.currentSubscription.planId.name}` : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  End: {subs.currentSubscription.endDate ? new Date(subs.currentSubscription.endDate).toLocaleDateString() : '—'}
                  {subs.currentSubscription.isTrial ? ' · Trial' : ''}
                  {isTeacherRoleSubscription(subs.currentSubscription) ? ' · Teacher plan' : ''}
                </Typography>
                {isTeacherRoleSubscription(subs.currentSubscription) ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Automatically attached for teachers. Only an administrator can remove this plan.
                  </Typography>
                ) : null}
                <Box mt={1} display="flex" flexWrap="wrap" gap={1} alignItems="center">
                  {!isTeacherRoleSubscription(subs.currentSubscription) ? (
                    <>
                      <TextField
                        size="small"
                        label="Extend trial (days)"
                        type="number"
                        value={extendDays}
                        onChange={(e) => setExtendDays(e.target.value)}
                        sx={{ width: 140 }}
                        inputProps={{ min: 1, max: 90 }}
                      />
                      <Button size="small" variant="outlined" onClick={() => handleExtendTrial(subs.currentSubscription._id)}>
                        Extend trial
                      </Button>
                    </>
                  ) : null}
                  {subs.currentSubscription.status === 'active' ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => handleCancelSub(subs.currentSubscription._id)}
                      sx={{ ml: 'auto' }}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => handleReactivateSub(subs.currentSubscription._id)}
                      sx={{ ml: 'auto' }}
                    >
                      Reactivate
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Typography color="text.secondary" gutterBottom>
                No active subscription.
              </Typography>
            )}

            <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Grant complimentary access
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Creates or extends access without payment (1–365 days).
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <TextField
                  size="small"
                  label="Days"
                  type="number"
                  value={compDays}
                  onChange={(e) => setCompDays(e.target.value)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 1, max: 365 }}
                />
                <Button size="small" variant="contained" onClick={handleGrantComp}>
                  Grant access
                </Button>
              </Box>
            </Paper>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} color="primary">
                All subscriptions
              </Typography>
              <DownloadCsvButton
                disabled={!(subs?.subscriptions || []).length}
                onClick={() =>
                  downloadCsv(
                    `${csvBase}-subscriptions`,
                    (subs?.subscriptions || []).map((s: any) => ({
                      status: s.status || '',
                      plan: s.planId?.name || '',
                      start: s.startDate ? new Date(s.startDate).toISOString() : '',
                      end: s.endDate ? new Date(s.endDate).toISOString() : '',
                      isTrial: s.isTrial ? 'yes' : 'no',
                      autoRenew: s.autoRenew ? 'yes' : 'no',
                      canceledAt: s.canceledAt ? new Date(s.canceledAt).toISOString() : '',
                    })),
                    ['status', 'plan', 'start', 'end', 'isTrial', 'autoRenew', 'canceledAt']
                  )
                }
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'primary.main' }}>Status</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Plan</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Start</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>End</TableCell>
                  <TableCell sx={{ color: 'primary.main' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(subs?.subscriptions || []).map((s: any) => (
                  <TableRow key={s._id}>
                    <TableCell>{s.status}</TableCell>
                    <TableCell>{s.planId?.name || '—'}</TableCell>
                    <TableCell>{s.startDate ? new Date(s.startDate).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{s.endDate ? new Date(s.endDate).toLocaleDateString() : '—'}</TableCell>
                    <TableCell align="right">
                      {s.status === 'canceled' || s.deletedAt ? (
                        <Button
                          size="small"
                          onClick={() => handleReactivateSub(s._id)}
                        >
                          Undo cancel
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} color="primary">
                History
              </Typography>
              <DownloadCsvButton
                disabled={!subHistory.length}
                onClick={() =>
                  downloadCsv(
                    `${csvBase}-subscription-history`,
                    subHistory.map((h) => ({
                      action: h.action || '',
                      description: h.description || '',
                      previousStatus: h.previousStatus || '',
                      newStatus: h.newStatus || '',
                      date: h.createdAt ? new Date(h.createdAt).toISOString() : '',
                    })),
                    ['action', 'description', 'previousStatus', 'newStatus', 'date']
                  )
                }
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'primary.main' }}>Action</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Description</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subHistory.map((h) => (
                  <TableRow key={h._id}>
                    <TableCell>{h.action}</TableCell>
                    <TableCell>{h.description || '—'}</TableCell>
                    <TableCell>{h.createdAt ? new Date(h.createdAt).toLocaleString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} color="primary">
                Transactions
              </Typography>
              <DownloadCsvButton
                disabled={!transactions.length}
                onClick={() =>
                  downloadCsv(
                    `${csvBase}-transactions`,
                    transactions.map((t) => ({
                      date: t.transactionDate ? new Date(t.transactionDate).toISOString() : '',
                      amount: t.amount ?? '',
                      currency: t.currency || '',
                      status: t.paymentStatus || '',
                      type: t.transactionType || '',
                      gateway: formatGateway(t.paymentMethod || t.gatewayName),
                      transactionId: t.transactionId || '',
                    })),
                    ['date', 'amount', 'currency', 'status', 'type', 'gateway', 'transactionId']
                  )
                }
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'primary.main' }}>Date</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Amount</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Status</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Type</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Gateway</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Transaction ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((t) => (
                  <TableRow key={t._id}>
                    <TableCell>{t.transactionDate ? new Date(t.transactionDate).toLocaleString() : '—'}</TableCell>
                    <TableCell>
                      {t.amount != null ? `${t.amount} ${t.currency || ''}` : '—'}
                    </TableCell>
                    <TableCell>{t.paymentStatus || '—'}</TableCell>
                    <TableCell>{t.transactionType || '—'}</TableCell>
                    <TableCell>{formatGateway(t.paymentMethod || t.gatewayName)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {t.transactionId || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        {activeTabKey === 'progression' && (
          <Paper sx={{ p: 2 }}>
            {tabLoading ? <CircularProgress size={24} /> : null}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Progression actions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Initialize empty progression from the active initial config. Manage configs in{' '}
                <Link component="button" type="button" onClick={() => navigateWithLang('/progression')}>
                  Progression admin
                </Link>
                .
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                <TextField
                  size="small"
                  label="Level"
                  type="number"
                  value={initLevel}
                  onChange={(e) => setInitLevel(e.target.value)}
                  sx={{ width: 100 }}
                  inputProps={{ min: 1 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleInitializeProgression}
                  disabled={initLoading}
                >
                  {initLoading ? 'Initializing…' : 'Initialize progression'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleAdvanceLevel}
                  disabled={advanceLoading}
                >
                  {advanceLoading ? 'Advancing…' : 'Advance level'}
                </Button>
              </Box>
            </Paper>
            {progStats ? (
              <Grid container spacing={2} mb={2}>
                <Grid item xs={6} sm={4} md={2}>
                  <Stat label="Level" value={progStats.currentLevel} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Stat label="XP" value={progStats.totalExperience} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Stat label="Completed" value={progStats.completedDocuments} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Stat label="In progress" value={progStats.inProgressDocuments} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Stat label="Total docs" value={progStats.totalDocuments} />
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Stat label="Badges" value={progStats.totalBadges} />
                </Grid>
              </Grid>
            ) : null}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Verified challenges (social)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Mark complete after the student leaves a Google review (or other admin-verified action). Grants 200 XP and the Google Review Champion badge.
              </Typography>
              {adminVerifiedChallenges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No admin-verified challenges in catalog. Run the Google review challenge seed on the server.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Challenge</TableCell>
                      <TableCell>Rewards</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {adminVerifiedChallenges.map(({ challenge, progress }) => {
                      const completed = progress?.rewardsGranted || progress?.status === 'completed';
                      const busy = challengeActionId === challenge._id;
                      const xp = challenge.rewards?.experiencePoints ?? 0;
                      const badgeCount = challenge.rewards?.badgeIds?.length ?? 0;
                      return (
                        <TableRow key={challenge._id}>
                          <TableCell>
                            <Typography variant="body2">
                              {challenge.locales?.en?.name || challenge.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {challenge.locales?.en?.description || challenge.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {xp > 0 ? `${xp} XP` : '—'}
                            {badgeCount > 0 ? ` + ${badgeCount} badge(s)` : ''}
                          </TableCell>
                          <TableCell>{completed ? 'Completed' : progress?.status || 'Not started'}</TableCell>
                          <TableCell align="right">
                            {completed ? (
                              <Typography variant="caption" color="text.secondary">
                                Done
                              </Typography>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={busy}
                                onClick={() =>
                                  handleCompleteUserChallenge(
                                    challenge._id,
                                    challenge.locales?.en?.name || challenge.name,
                                  )
                                }
                              >
                                {busy ? 'Working…' : 'Mark complete'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Special badges (admin-granted)
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Rare golden badges for Google reviews and our earliest long-standing students. Grant manually — they are never awarded automatically.
              </Typography>
              {manualBadges.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No admin-grant badges in catalog. Run the manual badge seed script on the server.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Badge</TableCell>
                      <TableCell>Rarity</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manualBadges.map((badge) => {
                      const earned = userEarnedBadgeIds.has(badge._id);
                      const busy = badgeActionId === badge._id;
                      return (
                        <TableRow key={badge._id}>
                          <TableCell>
                            <Typography variant="body2">{badge.locales?.en?.name || badge.name}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {badge.locales?.en?.description || badge.description}
                            </Typography>
                          </TableCell>
                          <TableCell>{badge.rarity || '—'}</TableCell>
                          <TableCell>{earned ? 'Earned' : 'Not earned'}</TableCell>
                          <TableCell align="right">
                            {earned ? (
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                disabled={busy}
                                onClick={() =>
                                  handleRevokeManualBadge(
                                    badge._id,
                                    badge.locales?.en?.name || badge.name,
                                  )
                                }
                              >
                                {busy ? 'Working…' : 'Revoke'}
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                disabled={busy}
                                onClick={() =>
                                  handleGrantManualBadge(
                                    badge._id,
                                    badge.locales?.en?.name || badge.name,
                                  )
                                }
                              >
                                {busy ? 'Working…' : 'Grant'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                XP adjustment
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} alignItems="center" mb={2}>
                <TextField
                  size="small"
                  label="Amount (+/-)"
                  type="number"
                  value={xpAdjustAmount}
                  onChange={(e) => setXpAdjustAmount(e.target.value)}
                  sx={{ width: 120 }}
                />
                <TextField
                  size="small"
                  label="Reason"
                  value={xpAdjustReason}
                  onChange={(e) => setXpAdjustReason(e.target.value)}
                  sx={{ flex: 1, minWidth: 180 }}
                />
                <Button size="small" variant="contained" onClick={handleXpAdjust} disabled={xpAdjustLoading}>
                  {xpAdjustLoading ? 'Applying…' : 'Apply'}
                </Button>
              </Box>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                XP history
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Reason / doc</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {xpHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No XP history</TableCell>
                    </TableRow>
                  ) : (
                    xpHistory.map((h, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{h.timestamp ? new Date(h.timestamp).toLocaleString() : '—'}</TableCell>
                        <TableCell>{h.source}</TableCell>
                        <TableCell>{h.amount > 0 ? `+${h.amount}` : h.amount}</TableCell>
                        <TableCell>{h.reason || h.documentCode || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Paper>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" color="primary">
                Recent document progress
              </Typography>
              <DownloadCsvButton
                disabled={progTotal === 0}
                onClick={handleDownloadProgressionsCsv}
                label="Download all (CSV)"
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'primary.main' }}>Code</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Domain</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Status</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Last accessed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {progressions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No document progress yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  progressions.map((p) => (
                    <TableRow key={p._id}>
                      <TableCell>{p.documentCode || '—'}</TableCell>
                      <TableCell>{p.documentDomain || '—'}</TableCell>
                      <TableCell>{p.isCompleted ? 'completed' : p.status || '—'}</TableCell>
                      <TableCell>{p.lastAccessedAt ? new Date(p.lastAccessedAt).toLocaleString() : '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={progTotal}
              page={progPage}
              onPageChange={(_, newPage) => loadProgressionsPage(newPage)}
              rowsPerPage={PROG_PAGE_SIZE}
              rowsPerPageOptions={[PROG_PAGE_SIZE]}
            />
          </Paper>
        )}

        {activeTabKey === 'sessions' && (
          <Paper sx={{ p: 2 }}>
            {tabLoading ? <CircularProgress size={24} /> : null}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6" color="primary">
                Sessions
              </Typography>
              <DownloadCsvButton
                disabled={!sessions.length}
                onClick={() =>
                  downloadCsv(
                    `${csvBase}-sessions`,
                    sessions.map((s) => ({
                      date: s.date ? new Date(s.date).toISOString() : '',
                      type: s.sessionType || '',
                      duration: s.duration ?? '',
                      attendance: s.attendanceStatus || '',
                      teacher: sessionTeacherName(s),
                      status: s.status || '',
                    })),
                    ['date', 'type', 'duration', 'attendance', 'teacher', 'status']
                  )
                }
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'primary.main' }}>Date</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Type</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Duration</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Attendance</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Teacher</TableCell>
                  <TableCell sx={{ color: 'primary.main' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No sessions found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((s) => (
                    <TableRow key={s._id}>
                      <TableCell>{s.date ? new Date(s.date).toLocaleString() : '—'}</TableCell>
                      <TableCell>{s.sessionType || '—'}</TableCell>
                      <TableCell>{s.duration != null ? `${s.duration} min` : '—'}</TableCell>
                      <TableCell>{s.attendanceStatus || '—'}</TableCell>
                      <TableCell>
                        {typeof s.profId === 'object' && s.profId !== null
                          ? s.profId.nickname || s.profId.email || '—'
                          : s.profId || '—'}
                      </TableCell>
                      <TableCell>{s.status || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        )}

        {activeTabKey === 'activity' && userId ? <UserAnalyticsPanel userId={userId} embedded /> : null}

        {activeTabKey === 'audit' && (
          <Paper sx={{ p: 2 }}>
            {tabLoading ? <CircularProgress size={24} /> : null}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">Admin audit trail</Typography>
              <DownloadCsvButton
                disabled={!auditLogs.length}
                onClick={() =>
                  downloadCsv(
                    `${csvBase}-audit`,
                    auditLogs.map((log) => ({
                      when: log.createdAt ? new Date(log.createdAt).toISOString() : '',
                      action: log.action || '',
                      admin: log.adminEmail || log.adminUserId || '',
                      details: log.metadata ? JSON.stringify(log.metadata) : '',
                    })),
                    ['when', 'action', 'admin', 'details']
                  )
                }
              />
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary">
                        No audit entries for this user.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {log.createdAt ? new Date(log.createdAt).toLocaleString() : '—'}
                      </TableCell>
                      <TableCell>{log.action}</TableCell>
                      <TableCell>{log.adminEmail || log.adminUserId}</TableCell>
                      <TableCell>
                        {log.metadata ? JSON.stringify(log.metadata).slice(0, 120) : '—'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>
    </Box>
    <ConfirmDeleteUserDialog
      open={deleteOpen}
      email={auth?.email || ''}
      displayName={summary?.identity.displayName || auth?.nickname || auth?.email || 'user'}
      loading={deleteLoading}
      error={deleteError}
      onClose={() => {
        if (!deleteLoading) {
          setDeleteOpen(false);
          setDeleteError(null);
        }
      }}
      onConfirm={handleDeleteUser}
    />
    </ContentPageLayout>
  );
};

const DownloadCsvButton: React.FC<{ onClick: () => void; disabled?: boolean; label?: string }> = ({
  onClick,
  disabled,
  label = 'Download CSV',
}) => (
  <Button size="small" variant="outlined" startIcon={<Download />} onClick={onClick} disabled={disabled}>
    {label}
  </Button>
);

const Stat: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6">{value}</Typography>
  </Paper>
);

const ListLinks: React.FC<{
  students: Array<{ studentId: string; studentNickname?: string; studentFirstName?: string; studentLastName?: string }>;
}> = ({ students }) => (
  <Box component="ul" sx={{ m: 0, pl: 2 }}>
    {students.map((s) => (
      <li key={s.studentId}>
        <Link component={RouterLink} to={`../users/${s.studentId}`}>
          {s.studentNickname || `${s.studentFirstName || ''} ${s.studentLastName || ''}`.trim() || s.studentId}
        </Link>
      </li>
    ))}
  </Box>
);

export default UserDetail;
